import { useLoading } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { useEffect, useMemo, useState } from 'react';
import { useDebounceCallback, useDebounceValue } from 'usehooks-ts';
import { useUpdateEffect } from '../../hooks/useCustomEffects';
import { getUsers } from '../../services/usersService';
import { IdName } from '../../types/app';
import { User } from '../../types/users';
import EnhancedLabeledSelect from '../ui/EnhancedLabeledSelect';

type Props = {
  selectedId?: string;
  onChangeId?: (id?: string) => void;
  onChange?: (data?: IdName) => void;
  isFilter?: boolean;
  label?: string;
  emptyOptionText?: string;
  showEmptyOption?: boolean;
  error?: React.ReactNode;
  onBlur?: () => void;
  inline?: boolean;
  clearable?: boolean;
  required?: boolean;
  maxDropdownHeight?: string;
  smallContainer?: boolean;
  dropdownOffset?: string;
};
export function UserSelect({
  selectedId,
  onChangeId,
  onChange,
  isFilter = true,
  label = 'Usuario:',
  emptyOptionText = 'Seleciona un usuario',
  showEmptyOption = false,
  error,
  onBlur,
  inline,
  clearable = false,
  required = false,
  maxDropdownHeight,
  smallContainer,
  dropdownOffset,
}: Readonly<Props>) {
  const [userList, setUserList] = useState<User[]>();
  const [page, setPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue<string | undefined>(undefined, 350);
  const { loading, startLoading, stopLoading } = useLoading();
  const [selectedOption, setSelectedOption] = useState<{ id: string; label: string }>();

  useUpdateEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    const loadUsers = async () => {
      startLoading();
      const { paginatedResponse, error } = await getUsers(page, debouncedSearch, 'active');
      if (!error && paginatedResponse) {
        if (page > 1 && userList) {
          setUserList([...userList, ...paginatedResponse.items]);
          setHasNextPage(paginatedResponse.hasNext);
        } else {
          setUserList(paginatedResponse.items);
          setHasNextPage(paginatedResponse.hasNext);
        }
      } else {
        setUserList(undefined);
        setHasNextPage(false);
      }
      stopLoading();
    };
    loadUsers();
  }, [page]);

  const userOptions = useMemo(() => {
    if (!userList) return;
    const coreOptions = userList.map((user) => ({ id: user._id, label: `${user.firstName} ${user.lastName}` }));
    const options = showEmptyOption
      ? [
          {
            id: undefined,
            label: isFilter ? 'Todos' : emptyOptionText,
          },
          ...coreOptions,
        ]
      : coreOptions;
    if (selectedOption) {
      if (!options.find((c) => c.id === selectedOption.id)) options.push(selectedOption);
    }
    return options;
  }, [userList]);

  const onSelectedChange = (params: OnChangeParams) => {
    if (params.option?.id) {
      onChangeId?.(params.option.id as string);
      onChange?.({ _id: params.option.id as string, name: params.option.label?.toString() ?? '' });
    } else {
      onChangeId?.(undefined);
      onChange?.(undefined);
    }
    setSelectedOption(params.option as { id: string; label: string });
  };

  const fetchData = () => {
    if (hasNextPage) setPage((page ?? 0) + 1);
  };

  const handleInputChange = useDebounceCallback(function (term: string) {
    setDebouncedSearch(term);
  }, 350);

  return (
    <EnhancedLabeledSelect
      label={label}
      options={userOptions}
      value={showEmptyOption || selectedId !== undefined ? [{ id: selectedId }] : undefined}
      onChange={onSelectedChange}
      fullWidth
      onInputChange={(e) => {
        handleInputChange(e.target.value);
      }}
      infiniteScroll
      fetchData={fetchData}
      fetchDataLoading={loading}
      hasNext={hasNextPage}
      onBlur={() => {
        onBlur && onBlur();
      }}
      searchable
      inline={inline}
      inset={inline}
      error={error}
      required={required}
      clearable={clearable}
      placeholder={emptyOptionText}
      maxDropdownHeight={maxDropdownHeight}
      smallContainer={smallContainer}
      dropdownOffset={dropdownOffset}
    />
  );
}
